import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';

import { useBillingUsage } from './use-billing-usage';
import { computeResource, type IResourceComputed } from '../utils/resource-health';

/** Resource key for metered outbound/inbound talk time (mirrors the SA + backend `RESOURCE_KEYS.VOICE_MINUTES`). */
export const VOICE_MINUTES_RESOURCE_KEY = 'voice_minutes';

export interface IVoiceMinutesStatus {
    /** The org's computed voice-minutes allocation — null when the plan doesn't meter minutes or usage is still loading. */
    readonly computed: IResourceComputed | null;
    /** Present AND capped (not an unlimited plan) — the only case where a dial can be blocked on minutes. */
    readonly isLimited: boolean;
    /** Limited AND no minutes left (ratio ≥ 1) — dialing should be gated. */
    readonly isExhausted: boolean;
    /** Limited AND ≥80% consumed but not yet exhausted — worth warning about. */
    readonly isNearLimit: boolean;
    readonly remaining: number | null;
    readonly isLoading: boolean;
    readonly isError: boolean;
}

/**
 * Reads the org's voice-minute balance off the shared billing-usage snapshot so
 * the call widget can warn/gate BEFORE the backend rejects a dial (50002). Never
 * reports exhausted while loading or on error — a missing snapshot must not
 * silently block calling, and the backend reserve remains the hard gate.
 */
export function useVoiceMinutesStatus(): IVoiceMinutesStatus {
    // The usage endpoint requires `billing:read`; a voice-only agent lacks it,
    // so skip the request for them (they fall back to the backend's hard 50002
    // reserve gate + toast) rather than 403-ing on every widget mount.
    const { hasPermission } = usePermissions();
    const { data, isLoading, isError } = useBillingUsage({
        enabled: hasPermission(PERMISSION.BILLING_READ),
    });
    const resource =
        data?.resources.find((r) => r.resourceKey === VOICE_MINUTES_RESOURCE_KEY) ?? null;
    const computed = resource ? computeResource(resource) : null;
    const isLimited = computed !== null && computed.limit !== null;

    return {
        computed,
        isLimited,
        isExhausted: isLimited && (computed?.remaining ?? 1) <= 0,
        isNearLimit: isLimited && computed?.health === 'warning',
        remaining: computed?.remaining ?? null,
        isLoading,
        isError,
    };
}
